// src/user/ReferralTreeVisual.js
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Stack,
  IconButton,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Avatar,
  Breadcrumbs,
  Link,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import Tree from "react-d3-tree";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { API } from "../config/apiConfig";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import FitScreenIcon from "@mui/icons-material/FitScreen";

// Build tree
function buildTree(data, currentUserId) {
  if (!Array.isArray(data)) return [];
  if (data.length === 0) return [];
  
  const lookup = {};
  const roots = [];
  
  console.log('🔨 [buildTree] Starting with', data.length, 'items');

  // First pass: create lookup - only process items with ReferralCode
  data.forEach((item) => {
    if (!item.ReferralCode) return; // Skip items without ReferralCode
    
    lookup[item.ReferralCode] = {
      id: item.UserID,
      name: `${item.FirstName || ""} ${item.LastName || ""}`.trim() || "Unknown",
      attributes: {
        isActive: item.IsActive,
        isRoot: item.UserID === currentUserId,
        userName: item.UserName || "",
        firstName: item.FirstName || "",
        lastName: item.LastName || "",
        profilePicture: item.ProfilePicture || item.AvatarUrl || item.ImageUrl || null,
        referralCode: item.ReferralCode,
      },
      children: [],
    };
  });

  // Second pass: build parent-child relationships
  const addedAsChild = new Set(); // Track which nodes have been added as children
  let connectionsMade = 0;
  let connectionsFailed = 0;
  
  data.forEach((item) => {
    if (!item.ReferralCode || !lookup[item.ReferralCode]) return; // Skip if no ReferralCode or not in lookup
    
    if (item.ReferredByCode && lookup[item.ReferredByCode]) {
      // This item has a parent - add it as a child
      lookup[item.ReferredByCode].children.push(lookup[item.ReferralCode]);
      addedAsChild.add(item.ReferralCode); // Mark as added as child
      connectionsMade++;
      
      // Log first few connections
      if (connectionsMade <= 5) {
        console.log('🔗 [buildTree] Connected:', {
          child: item.ReferralCode,
          childId: item.UserID,
          parent: item.ReferredByCode,
          parentChildrenNow: lookup[item.ReferredByCode].children.length
        });
      }
    } else if (item.ReferredByCode) {
      // Has ReferredByCode but parent not found in lookup
      connectionsFailed++;
      if (connectionsFailed <= 3) {
        console.warn('⚠️ [buildTree] Parent not found:', {
          child: item.ReferralCode,
          childId: item.UserID,
          parentCode: item.ReferredByCode,
          parentInLookup: !!lookup[item.ReferredByCode],
          availableCodes: Object.keys(lookup).slice(0, 5)
        });
      }
    }
  });
  
  // Third pass: add roots (only items that weren't added as children)
  data.forEach((item) => {
    if (!item.ReferralCode || !lookup[item.ReferralCode]) return;
    if (!addedAsChild.has(item.ReferralCode)) {
      // This item wasn't added as a child, so it's a root
      if (!roots.find(r => r.id === lookup[item.ReferralCode].id)) {
        roots.push(lookup[item.ReferralCode]);
      }
    }
  });
  
  console.log('🔨 [buildTree] After building:', {
    lookupSize: Object.keys(lookup).length,
    rootsCount: roots.length,
    addedAsChildCount: addedAsChild.size,
    connectionsMade: connectionsMade,
    connectionsFailed: connectionsFailed,
    rootChildren: roots[0]?.children?.length || 0,
    currentUserInRoots: roots.find(r => r.id === currentUserId) ? 'YES' : 'NO'
  });

  // Sort children optimally to minimize gaps
  function sortChildren(node) {
    if (!node.children || node.children.length === 0) return;
    
    // Recursively sort all descendants first
    node.children.forEach(sortChildren);
    
    // Separate children into those with and without downlines
    const withDownlines = [];
    const withoutDownlines = [];
    
    node.children.forEach((child) => {
      const descendantCount = countDescendants(child);
      if (descendantCount > 0) {
        withDownlines.push({ node: child, count: descendantCount });
      } else {
        withoutDownlines.push(child);
      }
    });
    
    // Sort nodes with downlines by size (largest first)
    withDownlines.sort((a, b) => b.count - a.count);
    
    // If no nodes with downlines, just use the original order
    if (withDownlines.length === 0) {
      node.children = withoutDownlines;
      return;
    }
    
    // If no nodes without downlines, just use the sorted with downlines
    if (withoutDownlines.length === 0) {
      node.children = withDownlines.map(item => item.node);
      return;
    }
    
    // Calculate how many nodes without downlines to place between each node with downlines
    const totalSlots = withDownlines.length + 1; // spaces before, between, and after nodes with downlines
    const perSlot = Math.floor(withoutDownlines.length / totalSlots);
    const remainder = withoutDownlines.length % totalSlots;
    
    const result = [];
    let withoutIndex = 0;
    
    // Distribute nodes without downlines evenly
    for (let i = 0; i < withDownlines.length; i++) {
      // Add some nodes without downlines before this node with downlines
      const nodesToAddBefore = perSlot + (i < remainder ? 1 : 0);
      for (let j = 0; j < nodesToAddBefore && withoutIndex < withoutDownlines.length; j++) {
        result.push(withoutDownlines[withoutIndex++]);
      }
      
      // Add the node with downlines
      result.push(withDownlines[i].node);
    }
    
    // Add remaining nodes without downlines at the end
    while (withoutIndex < withoutDownlines.length) {
      result.push(withoutDownlines[withoutIndex++]);
    }
    
    node.children = result;
  }

  // Apply sorting to all roots
  roots.forEach(sortChildren);

  // Ensure current user is the root if they exist
  const currentUserNode = roots.find(r => r.id === currentUserId) || 
    Object.values(lookup).find(node => node.id === currentUserId);
  
  if (currentUserNode) {
    // Return current user as the single root (their children are already attached)
    return [currentUserNode];
  }

  // If current user not found, return all roots
  return roots;
}

// Descendant count (moved before buildTree since it's used there)
function countDescendants(node) {
  if (!node.children || node.children.length === 0) return 0;
  return (
    node.children.length +
    node.children.reduce((sum, child) => sum + countDescendants(child), 0)
  );
}

// Limit depth - default to 20 levels (but can be overridden for performance)
function limitDepth(node, depth, maxDepth = 20) {
  if (depth >= maxDepth) {
    node.children = [];
  } else {
    node.children?.forEach((child) => limitDepth(child, depth + 1, maxDepth));
  }
}

// Count levels - support up to 20 levels
function countLevels(root, maxDepth = 20) {
  const counts = Array(maxDepth).fill(0);
  function traverse(node, depth) {
    if (depth > maxDepth) return;
    if (depth > 0) counts[depth - 1] += 1;
    node.children?.forEach((child) => traverse(child, depth + 1));
  }
  traverse(root, 0);
  return counts;
}

// Find path for search - searches all levels without depth restrictions
function findPathToNode(node, searchTerm, path = [], maxDepth = 100) {
  if (!node) return null;
  
  const matchString = `${node.name || ''} ${node.attributes?.userName || ''}`.toLowerCase();
  if (matchString.includes(searchTerm.toLowerCase())) {
    return [...path, node.id];
  }
  
  // Recursively search all children, no depth limit for search
  if (node.children && Array.isArray(node.children) && node.children.length > 0) {
    for (let child of node.children) {
      const result = findPathToNode(child, searchTerm, [...path, node.id], maxDepth);
      if (result) return result;
    }
  }
  
  return null;
}

// Get all descendant IDs
function getAllDescendantIds(node) {
  const ids = [node.id];
  if (node.children && node.children.length > 0) {
    node.children.forEach(child => {
      ids.push(...getAllDescendantIds(child));
    });
  }
  return ids;
}

// Find node by ID in tree
function findNodeById(tree, nodeId) {
  if (!tree) return null;
  
  for (let root of tree) {
    if (root.id === nodeId) return root;
    
    const found = searchInNode(root, nodeId);
    if (found) return found;
  }
  return null;
}

function searchInNode(node, nodeId) {
  if (node.id === nodeId) return node;
  if (!node.children) return null;
  
  for (let child of node.children) {
    const found = searchInNode(child, nodeId);
    if (found) return found;
  }
  return null;
}

const ReferralTreeVisual = () => {
  const theme = useTheme();
  const isMobileQuery = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Aggressive mobile detection - check multiple methods
  const detectMobile = () => {
    if (typeof window === 'undefined') return false;
    const width = window.innerWidth;
    const ua = navigator.userAgent || navigator.vendor || window.opera || '';
    const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua.toLowerCase());
    const isSmallScreen = width < 768; // More lenient threshold
    return isSmallScreen || isMobileUA;
  };
  
  const [isMobile, setIsMobile] = useState(detectMobile);

  const [treeData, setTreeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [zoom, setZoom] = useState(() => {
    // Start with a zoom that shows the user's node well on mobile
    if (typeof window !== 'undefined' && window.innerWidth <= 600) {
      return 0.6; // Zoom out more on mobile to show the user's node
    }
    return 0.8; // Standard zoom for desktop
  });
  const [levelCounts, setLevelCounts] = useState([]);
  const [search, setSearch] = useState("");
  const [highlightedId, setHighlightedId] = useState(null);
  const [clickedNodeId, setClickedNodeId] = useState(null);
  const [highlightedDownlineIds, setHighlightedDownlineIds] = useState(new Set());
  const [focusedNodeId, setFocusedNodeId] = useState(null);
  const [navigationPath, setNavigationPath] = useState([]); // Track navigation path for breadcrumbs
  const [lastClickTime, setLastClickTime] = useState(0);

  const treeContainerRef = useRef();
  const svgRef = useRef(null);
  const touchStateRef = useRef({ isPanning: false, startDistance: 0, startZoom: 0.8 });
  const [containerWidth, setContainerWidth] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth || 500;
    }
    return 500;
  });

  // Inject mobile-specific global styles (zoom-friendly version)
  useEffect(() => {
    if (isMobile) {
      const styleId = 'referral-tree-mobile-styles';
      // Remove old style if exists
      const oldStyle = document.getElementById(styleId);
      if (oldStyle) {
        oldStyle.remove();
      }
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @media (max-width: 768px) {
          .rd3t-tree-container {
            width: 100% !important;
            height: 100% !important;
          }
          /* REMOVED: transform: scale(1) !important - this was blocking zoom! */
          foreignObject {
            width: 220px !important;
            height: 180px !important;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, [isMobile]);

  // Update mobile detection and container width on resize
  useEffect(() => {
    const updateDimensions = () => {
      const isMobileDevice = detectMobile();
      setIsMobile(isMobileDevice);
      
      // Debug log for mobile detection
      if (isMobileDevice) {
        console.log('📱 Mobile detected:', {
          width: window.innerWidth,
          ua: navigator.userAgent,
          isMobileQuery,
          isMobile: isMobileDevice
        });
      }
      
      if (treeContainerRef.current) {
        const containerWidthValue = treeContainerRef.current.clientWidth || window.innerWidth || 500;
        setContainerWidth(containerWidthValue);
      }
    };
    
    // Run immediately
    updateDimensions();
    
    // Also run after a short delay to catch any late initialization
    const timeoutId = setTimeout(updateDimensions, 100);
    
    window.addEventListener('resize', updateDimensions);
    window.addEventListener('orientationchange', updateDimensions);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateDimensions);
      window.removeEventListener('orientationchange', updateDimensions);
    };
  }, [isMobileQuery, zoom]);

  // Extract fetchTree function so it can be called manually for refresh
  const fetchTree = useCallback(async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      const token = localStorage.getItem("token");
      if (!token) {
        setError("No token found. Please log in again.");
        setLoading(false);
        return;
      }

      const decoded = jwtDecode(token);
      const userId = decoded.UserID || decoded.userId;
      setCurrentUserId(userId);

      const response = await axios.get(API.referralsTree(userId), {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log('📡 [fetchTree] Full response object:', response);
      console.log('📡 [fetchTree] response.data:', response.data);
      console.log('📡 [fetchTree] response.data type:', typeof response.data);
      console.log('📡 [fetchTree] response.data isArray:', Array.isArray(response.data));
      if (response.data) {
        console.log('📡 [fetchTree] response.data keys:', Object.keys(response.data));
        console.log('📡 [fetchTree] response.data.success:', response.data.success);
        console.log('📡 [fetchTree] response.data.tree:', response.data.tree);
        console.log('📡 [fetchTree] response.data.tree type:', typeof response.data.tree);
        console.log('📡 [fetchTree] response.data.tree isArray:', Array.isArray(response.data.tree));
        console.log('📡 [fetchTree] response.data.tree length:', response.data.tree?.length);
        if (response.data.tree && Array.isArray(response.data.tree)) {
          console.log('📡 [fetchTree] First 5 items in tree:', response.data.tree.slice(0, 5));
          console.log('📡 [fetchTree] ALL items in tree:', response.data.tree);
        }
      }

      // Handle different response formats
      let raw = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          // Response is directly an array
          raw = response.data;
          console.log('📡 [fetchTree] Response is direct array, length:', raw.length);
        } else if (response.data.success && Array.isArray(response.data.tree)) {
          // Standard format: { success: true, tree: [...] }
          raw = response.data.tree;
          console.log('📡 [fetchTree] Response has tree array, length:', raw.length);
        } else if (Array.isArray(response.data.tree)) {
          // Tree exists but no success flag
          raw = response.data.tree;
          console.log('📡 [fetchTree] Response has tree array (no success flag), length:', raw.length);
        } else if (response.data.recordset && Array.isArray(response.data.recordset)) {
          // Direct recordset
          raw = response.data.recordset;
          console.log('📡 [fetchTree] Response has recordset, length:', raw.length);
        } else {
          console.error('📡 [fetchTree] Unknown response format:', response.data);
        }
      }
      
      console.log('📡 [fetchTree] Final raw data length:', raw.length);
      if (raw.length > 0) {
        console.log('📡 [fetchTree] First 3 items:', raw.slice(0, 3));
      }
      
      if (raw.length > 0) {
        // Show loading state while processing
        if (isMobile) {
          console.log('📱 Processing tree for mobile...');
        }
        
        const nested = buildTree(raw, userId);

        if (nested[0]) {
          // Store full tree without depth limits - we'll limit display only
          // This allows focusing to show full subtrees
          setLevelCounts(countLevels(nested[0], 20)); // Count all levels
          
          if (isMobile) {
            console.log('📱 Tree processed, setting data (full tree preserved)...');
          }
        }

        // Store full tree - no depth limiting in stored data
        // Depth limiting will be applied only in display/filtering if needed
        setTreeData(nested);
        
        // Clear any focused/selected states when refreshing
        setFocusedNodeId(null);
        setNavigationPath([]);
        setClickedNodeId(null);
        setHighlightedDownlineIds(new Set());
        setHighlightedId(null);
        setSearch("");
      } else {
        setError("Failed to load referral tree - no data received.");
      }
    } catch (err) {
      console.error("❌ Error fetching referral tree:", err);
      setError("Server error fetching referral tree.");
    } finally {
      setLoading(false);
    }
  }, [isMobile]);

  // Fetch tree on mount
  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  // Center and set appropriate zoom on mobile when tree data loads
  useEffect(() => {
    if (treeData && treeData.length > 0 && isMobileQuery) {
      // Set zoom to show user's node clearly on mobile
      setZoom(0.6);
      // Small delay to ensure tree is rendered before adjusting
      const timer = setTimeout(() => {
        // The translate is already set responsively, but we ensure zoom is correct
        setZoom(0.6);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [treeData, isMobileQuery]);

  // Memoized level calculation for better performance
  const nodeLevels = useMemo(() => {
    if (!treeData || !treeData[0]) return {};
    
    const levels = {};
    function calculateLevels(node, depth = 0) {
      levels[node.id] = depth;
      node.children?.forEach(child => calculateLevels(child, depth + 1));
    }
    calculateLevels(treeData[0]);
    return levels;
  }, [treeData]);

  // Filter tree data based on focused node and apply display limits
  const filteredTreeData = useMemo(() => {
    if (!treeData) return null;
    
    let treeToDisplay = treeData;
    
    // If focused, show only the focused node and its descendants
    if (focusedNodeId) {
      const focusedNode = findNodeById(treeData, focusedNodeId);
      if (focusedNode) {
        // Deep clone the focused node to preserve ALL nested children
        const cloneNodeRecursively = (node) => {
          if (!node) return null;
          const cloned = {
            ...node,
            children: node.children && node.children.length > 0
              ? node.children.map(child => cloneNodeRecursively(child))
              : []
          };
          return cloned;
        };
        treeToDisplay = [cloneNodeRecursively(focusedNode)];
      }
    }
    
    // Show full tree on all devices (no depth limits)
    return treeToDisplay;
  }, [treeData, focusedNodeId, isMobile]);

  // Handle node click to highlight downlines (also detects double-click)
  const handleNodeClick = useCallback((nodeId) => {
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTime;
    
    // Get the node to check if it has downlines
    const node = findNodeById(treeData, nodeId);
    if (!node) return;
    
    const hasDownlines = node.children && node.children.length > 0;
    
    // Double-click detection (within 300ms)
    if (timeSinceLastClick < 300 && clickedNodeId === nodeId) {
      // Double-click: Focus on this user and show their downlines (only if they have downlines)
      if (hasDownlines) {
        if (focusedNodeId === nodeId) {
          // Already focused - go back one level in navigation
          if (navigationPath.length > 0) {
            // Go back to previous level
            const previousPath = navigationPath.slice(0, -1);
            setNavigationPath(previousPath);
            setFocusedNodeId(previousPath.length > 0 ? previousPath[previousPath.length - 1] : null);
          } else {
            // Go back to full tree
            setFocusedNodeId(null);
            setNavigationPath([]);
          }
        } else {
          // Entering focused view - add to navigation path
          const newPath = focusedNodeId 
            ? [...navigationPath, focusedNodeId, nodeId] 
            : [nodeId];
          setNavigationPath(newPath);
          setFocusedNodeId(nodeId);
          setZoom(isMobileQuery ? 0.6 : 0.8);
        }
        setClickedNodeId(null);
        setHighlightedDownlineIds(new Set());
      }
      setLastClickTime(0);
    } else {
      // Single click: Highlight downlines (only if they have downlines)
      setLastClickTime(now);
      
      if (clickedNodeId === nodeId) {
        // Clicking same node again - clear highlight
        setClickedNodeId(null);
        setHighlightedDownlineIds(new Set());
      } else {
        // Clicking new node - highlight it and all downlines (only if they have downlines)
        if (hasDownlines) {
          const downlineIds = getAllDescendantIds(node);
          setClickedNodeId(nodeId);
          setHighlightedDownlineIds(new Set(downlineIds));
        }
      }
    }
  }, [treeData, clickedNodeId, lastClickTime, focusedNodeId, navigationPath]);

  // 🎨 Custom node renderer
  const renderCustomNode = useCallback(
    ({ nodeDatum }) => {
      const { name, attributes, id } = nodeDatum;
      const isActive = attributes?.isActive !== false;
      const isRoot = attributes?.isRoot;
      const totalDescendants = countDescendants(nodeDatum);
      const isHighlighted = id === highlightedId;
      const isClickedNode = id === clickedNodeId;
      const isInDownline = highlightedDownlineIds.has(id);
      const isFocusedNode = id === focusedNodeId;
      const hasDownlines = totalDescendants > 0;
      const level = nodeLevels[id] || 0;

      // Node sizes - same for all devices
      const nodeWidth = 260;
      const nodeHeight = 200;
      const nodePadding = 1.5;

      return (
        <foreignObject width={nodeWidth} height={nodeHeight} x={-nodeWidth/2} y={-nodeHeight/2}>
          <Paper
            elevation={isHighlighted || isInDownline || isFocusedNode ? 6 : 4}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleNodeClick(id);
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleNodeClick(id);
            }}
            sx={{
              p: nodePadding,
              borderRadius: 2,
              border: "3px solid",
              borderColor: isFocusedNode
                ? "info.main"
                : isClickedNode
                ? "error.main"
                : isInDownline
                ? "secondary.main"
                : isHighlighted
                ? "warning.main"
                : isRoot
                ? "primary.main"
                : isActive
                ? "success.main"
                : "grey.400",
              bgcolor: isFocusedNode
                ? "info.light"
                : isInDownline 
                ? "secondary.light" 
                : isActive 
                ? "background.paper" 
                : "grey.100",
              textAlign: "center",
              position: "relative",
              minHeight: 180,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              cursor: hasDownlines ? "pointer" : "default",
              touchAction: "none",
              WebkitTapHighlightColor: "transparent",
              opacity: hasDownlines ? 1 : 0.85,
              transition: "all 0.3s ease",
              "&:hover": hasDownlines ? {
                transform: "scale(1.05)",
                boxShadow: 4,
              } : {},
              "&:active": hasDownlines ? {
                transform: "scale(1.02)",
              } : {},
            }}
            ref={(el) => {
              if (isHighlighted && el && treeContainerRef.current) {
                // Auto-scroll to highlighted node
                const container = treeContainerRef.current;
                const box = el.getBoundingClientRect();
                const containerBox = container.getBoundingClientRect();
                container.scrollTop += box.top - containerBox.top - container.clientHeight / 2 + box.height / 2;
                container.scrollLeft += box.left - containerBox.left - container.clientWidth / 2 + box.width / 2;
              }
            }}
          >
            {isFocusedNode && (
              <Chip
                label="Focused"
                size="small"
                color="info"
                sx={{ position: "absolute", top: -12, right: -12 }}
              />
            )}
            
            {isRoot && !isFocusedNode && (
              <Chip
                label="You"
                size="small"
                color="primary"
                sx={{ position: "absolute", top: -12, right: -12 }}
              />
            )}

            {/* Level Indicator */}
            {!isRoot && (
              <Chip
                label={`L${level}`}
                size="small"
                color="info"
                sx={{ position: "absolute", top: -12, left: -12 }}
              />
            )}

            {/* Avatar/Profile Picture */}
            {attributes?.profilePicture ? (
              <Avatar
                src={attributes.profilePicture}
                alt={name}
                sx={{
                  width: 50,
                  height: 50,
                  mx: "auto",
                  mb: 0.5,
                }}
              >
                {name.charAt(0).toUpperCase()}
              </Avatar>
            ) : (
              <Avatar
                sx={{
                  width: 50,
                  height: 50,
                  mx: "auto",
                  mb: 0.5,
                  bgcolor: isRoot ? "primary.main" : "secondary.main",
                }}
              >
                {name.charAt(0).toUpperCase()}
              </Avatar>
            )}
            
            <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: "0.875rem" }}>
              {name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
              @{attributes?.userName}
            </Typography>

            {totalDescendants > 0 && (
              <Stack direction="row" justifyContent="center" mt={0.5}>
                <Chip
                  label={`${totalDescendants} referrals`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Stack>
            )}
          </Paper>
        </foreignObject>
      );
    },
    [highlightedId, nodeLevels, clickedNodeId, highlightedDownlineIds, focusedNodeId, handleNodeClick]
  );

  // Line styles
  const pathClassFunc = (linkData) => {
    const targetActive = linkData.target.data.attributes?.isActive !== false;
    return targetActive ? "link-active" : "link-inactive";
  };

  const treeStyles = {
    links: {
      ".link-active": { stroke: "green", strokeWidth: 2 },
      ".link-inactive": { stroke: "grey", strokeWidth: 2, strokeDasharray: "4,2" },
    },
  };

  // Handle search - searches the FULL tree data without any depth restrictions
  const handleSearch = () => {
    if (!treeData || treeData.length === 0 || !search.trim()) {
      setHighlightedId(null);
      setFocusedNodeId(null);
      setNavigationPath([]);
      setClickedNodeId(null);
      setHighlightedDownlineIds(new Set());
      return;
    }

    // Search through all root nodes (typically just one, but handle multiple)
    let path = null;
    for (let rootNode of treeData) {
      path = findPathToNode(rootNode, search.trim());
      if (path) break; // Found a match, stop searching
    }

    if (path && path.length > 0) {
      const foundNodeId = path[path.length - 1];
      
      // Focus on the found node - this will show them and their downline
      setFocusedNodeId(foundNodeId);
      
      // Build navigation path (all nodes from root to the found node, excluding the found node itself)
      const navPath = path.slice(0, -1);
      setNavigationPath(navPath);
      
      // Highlight the found node
      setHighlightedId(foundNodeId);
      
      // Clear any previous click/highlight states
      setClickedNodeId(null);
      setHighlightedDownlineIds(new Set());
      
      // Reset zoom for better visibility
      setZoom(isMobileQuery ? 0.6 : 0.8);
    } else {
      // No match found - clear everything
      setHighlightedId(null);
      setFocusedNodeId(null);
      setNavigationPath([]);
      setClickedNodeId(null);
      setHighlightedDownlineIds(new Set());
    }
  };

  // Touch handlers for mobile - ignore touches on buttons
  const handleTouchStart = useCallback((e) => {
    // Don't interfere if touch starts on a button or control element
    const target = e.target;
    if (
      target.closest('button') || 
      target.closest('[role="button"]') || 
      target.closest('.MuiIconButton-root') ||
      target.closest('[aria-label*="Zoom"]') ||
      target.closest('[aria-label*="Reset"]')
    ) {
      return; // Let the button handle it
    }
    
    if (e.touches.length === 2) {
      // Two-finger pinch
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      touchStateRef.current.startDistance = distance;
      touchStateRef.current.startZoom = zoom;
      e.preventDefault();
    } else if (e.touches.length === 1) {
      // Single finger - allow panning via scroll
      touchStateRef.current.isPanning = true;
    }
  }, [zoom]);

  const handleTouchMove = useCallback((e) => {
    // Don't interfere if touch is on a button
    const target = e.target;
    if (target.closest('button') || target.closest('[role="button"]') || target.closest('.MuiIconButton-root')) {
      return;
    }
    
    if (e.touches.length === 2) {
      // Pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      const scale = distance / touchStateRef.current.startDistance;
      const newZoom = Math.max(0.3, Math.min(2, touchStateRef.current.startZoom * scale));
      setZoom(newZoom);
      e.preventDefault();
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    touchStateRef.current.isPanning = false;
    touchStateRef.current.startDistance = 0;
  }, []);

  // Desktop zoom handlers - keep simple
  const handleZoomIn = useCallback(() => {
    setZoom((z) => Math.min(2, z + 0.2));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((z) => Math.max(0.3, z - 0.2));
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoom(isMobileQuery ? 0.6 : 0.8);
  }, [isMobileQuery]);

  // Helper to collect all node IDs for default expansion (mobile)
  // When focused, show all levels (at least 5); otherwise limit to 5 for performance
  const getAllNodeIds = useCallback((node, level = 0, maxLevel = focusedNodeId ? 20 : 5) => {
    if (!node || level > maxLevel) return [];
    const ids = [`node-${node.id}`];
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        ids.push(...getAllNodeIds(child, level + 1, maxLevel));
      });
    }
    return ids;
  }, [focusedNodeId]);

  // Helper to count descendants for mobile display
  const countDescendantsMobile = useCallback((node) => {
    if (!node || !node.children) return 0;
    let count = node.children.length;
    node.children.forEach(child => {
      count += countDescendantsMobile(child);
    });
    return count;
  }, []);

  // Mobile TreeView renderer - simple tree with left/right branching
  const renderMobileTreeNode = useCallback((node, level = 0) => {
    if (!node) return null;
    
    const { id, name, attributes, children } = node;
    const isRoot = attributes?.isRoot;
    const isActive = attributes?.isActive !== false;
    const hasChildren = children && children.length > 0;
    const nodeId = `node-${id}`;
    const displayName = name || `User ${id}`;
    const userName = attributes?.userName || `user${id}`;
    const firstName = attributes?.firstName || "";
    const lastName = attributes?.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim() || displayName;
    
    // Highlighting logic
    const isClickedNode = id === clickedNodeId;
    const isInDownline = highlightedDownlineIds.has(id);
    const totalDescendants = countDescendantsMobile(node);
    
    // Visual hierarchy colors based on level
    const levelColors = {
      0: { bg: "primary.main", border: "primary.dark" },
      1: { bg: "success.main", border: "success.dark" },
      2: { bg: "info.main", border: "info.dark" },
      3: { bg: "warning.main", border: "warning.dark" },
      4: { bg: "secondary.main", border: "secondary.dark" },
    };
    const colorIndex = level > 4 ? ((level - 5) % 5) + 1 : level;
    const levelColor = levelColors[colorIndex] || levelColors[4];
    
    // Split children into left and right
    const midPoint = Math.ceil((hasChildren ? children.length : 0) / 2);
    const leftChildren = hasChildren ? children.slice(0, midPoint) : [];
    const rightChildren = hasChildren ? children.slice(midPoint) : [];
    
    return (
      <Box
        key={nodeId}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
          mb: level === 0 ? 2 : 1.5,
          mt: level === 0 ? 0 : 1,
        }}
      >
        {/* Node Card */}
        <Paper
          elevation={isClickedNode ? 6 : isInDownline ? 4 : 2}
          onClick={(e) => {
            e.stopPropagation();
            handleNodeClick(id);
          }}
          sx={{
            p: 1.2,
            minWidth: "130px",
            maxWidth: "150px",
            borderRadius: 2,
            border: isClickedNode 
              ? `3px solid ${theme.palette.primary.main}` 
              : isInDownline 
                ? `2px solid ${theme.palette.primary.light}` 
                : `2px solid ${isActive ? levelColor.border : theme.palette.grey[300]}`,
            backgroundColor: isClickedNode 
              ? "primary.light" 
              : isInDownline 
                ? "action.selected" 
                : "background.paper",
            boxShadow: isClickedNode ? 4 : 2,
            transition: "all 0.2s ease",
            cursor: "pointer",
            "&:active": {
              transform: "scale(0.95)",
            },
          }}
        >
          {/* Avatar */}
          <Box display="flex" justifyContent="center" mb={0.5}>
            <Avatar
              sx={{
                width: 42,
                height: 42,
                bgcolor: isClickedNode 
                  ? "primary.main" 
                  : isRoot 
                    ? "primary.main" 
                    : levelColor.bg,
                fontSize: "1rem",
                fontWeight: "bold",
                boxShadow: isClickedNode ? 3 : 1,
                border: `2px solid ${isClickedNode ? theme.palette.primary.dark : "transparent"}`,
              }}
            >
              {displayName.charAt(0).toUpperCase()}
            </Avatar>
          </Box>
          
          {/* Full Name */}
          <Typography 
            variant="body2" 
            fontWeight={isClickedNode ? "bold" : "medium"}
            textAlign="center"
            sx={{
              fontSize: "0.75rem",
              color: isClickedNode ? "primary.main" : "text.primary",
              mb: 0.25,
              lineHeight: 1.2,
            }}
          >
            {fullName}
          </Typography>
          
          {/* Username */}
          <Typography 
            variant="caption" 
            color="text.secondary" 
            textAlign="center"
            noWrap
            sx={{ fontSize: "0.65rem", display: "block", mb: 0.5 }}
          >
            @{userName}
          </Typography>
          
          {/* Chips */}
          <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" gap={0.25}>
            {isRoot && (
              <Chip label="You" size="small" color="primary" sx={{ fontSize: "0.65rem", height: "18px" }} />
            )}
            {!isRoot && (
              <Chip
                label={`L${level}`}
                size="small"
                color={level === 1 ? "success" : level === 2 ? "info" : level === 3 ? "warning" : level === 4 ? "secondary" : "default"}
                sx={{ fontSize: "0.65rem", height: "18px" }}
              />
            )}
            {isActive && (
              <Chip label="Active" size="small" color="success" sx={{ fontSize: "0.6rem", height: "16px" }} />
            )}
          </Stack>
          
          {/* Referral Count */}
          {hasChildren && (
            <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ display: "block", mt: 0.5, fontSize: "0.65rem" }}>
              {totalDescendants} {totalDescendants === 1 ? 'ref' : 'refs'}
            </Typography>
          )}
        </Paper>
        
        {/* Connecting Line (from parent to this node) */}
        {level > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: "-12px",
              width: "2px",
              height: "12px",
              backgroundColor: levelColor.border,
              opacity: 0.6,
            }}
          />
        )}
        
        {/* Children Container - Left and Right */}
        {hasChildren && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
              mt: 1,
              position: "relative",
              minWidth: "200px",
            }}
          >
            {/* Vertical line from parent to children */}
            <Box
              sx={{
                position: "absolute",
                top: "-8px",
                left: "50%",
                transform: "translateX(-50%)",
                width: "2px",
                height: "8px",
                backgroundColor: levelColor.border,
                opacity: 0.6,
              }}
            />
            
            {/* Horizontal line connecting children */}
            {children.length > 1 && (
              <Box
                sx={{
                  position: "absolute",
                  top: "-8px",
                  left: `${leftChildren.length > 0 ? "25%" : "50%"}`,
                  right: `${rightChildren.length > 0 ? "25%" : "50%"}`,
                  height: "2px",
                  backgroundColor: levelColor.border,
                  opacity: 0.6,
                }}
              />
            )}
            
            {/* Left Children */}
            {leftChildren.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flex: 1, mr: 0.5, pr: 1 }}>
                {leftChildren.map((child) => (
                  <Box key={child.id} sx={{ position: "relative", mb: 0.5 }}>
                    {/* Connecting line from horizontal to node */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: "-8px",
                        right: "50%",
                        width: "2px",
                        height: "8px",
                        backgroundColor: levelColor.border,
                        opacity: 0.6,
                      }}
                    />
                    {renderMobileTreeNode(child, level + 1)}
                  </Box>
                ))}
              </Box>
            )}
            
            {/* Right Children */}
            {rightChildren.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", flex: 1, ml: 0.5, pl: 1 }}>
                {rightChildren.map((child) => (
                  <Box key={child.id} sx={{ position: "relative", mb: 0.5 }}>
                    {/* Connecting line from horizontal to node */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: "-8px",
                        left: "50%",
                        width: "2px",
                        height: "8px",
                        backgroundColor: levelColor.border,
                        opacity: 0.6,
                      }}
                    />
                    {renderMobileTreeNode(child, level + 1)}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  }, [handleNodeClick, clickedNodeId, highlightedDownlineIds, theme, countDescendantsMobile]);


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!treeData || treeData.length === 0) {
    return (
      <Box mt={3}>
        <Alert severity="info">No referral tree found.</Alert>
      </Box>
    );
  }

  return (
    <Box 
      p={1} 
      height="95vh" 
      minHeight="auto"
      width="100%" 
      maxWidth="100vw"
      display="flex" 
      flexDirection="column" 
      sx={{ 
        overflow: "hidden",
        position: "relative",
        boxSizing: "border-box",
        WebkitOverflowScrolling: "touch",
        overflowX: "hidden",
      }}
    >
      {/* Header with Search and Controls */}
      <Stack 
        direction="row" 
        spacing={2} 
        mb={1} 
        alignItems="center" 
        justifyContent="space-between" 
        flexWrap="wrap"
        sx={{ px: 0, flexShrink: 0 }}
      >
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          {focusedNodeId && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <IconButton
                size="small"
                onClick={() => {
                  if (navigationPath.length > 0) {
                    const previousPath = navigationPath.slice(0, -1);
                    setNavigationPath(previousPath);
                    setFocusedNodeId(previousPath.length > 0 ? previousPath[previousPath.length - 1] : null);
                  } else {
                    setFocusedNodeId(null);
                    setNavigationPath([]);
                  }
                  setClickedNodeId(null);
                  setHighlightedDownlineIds(new Set());
                }}
                sx={{ 
                  backgroundColor: "primary.main",
                  color: "white",
                  "&:hover": { backgroundColor: "primary.dark" }
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  setFocusedNodeId(null);
                  setNavigationPath([]);
                  setClickedNodeId(null);
                  setHighlightedDownlineIds(new Set());
                }}
                sx={{ 
                  backgroundColor: "secondary.main",
                  color: "white",
                  "&:hover": { backgroundColor: "secondary.dark" }
                }}
              >
                <HomeIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </Box>
        
        {/* Search and Controls */}
        <Stack 
          direction="row" 
          spacing={1} 
          alignItems="center" 
          flexWrap="wrap" 
          gap={1}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          {/* Search */}
          <TextField
            label="Search by name or username"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            sx={{ width: { xs: '100%', sm: 300 }, flex: { xs: 1, sm: 'none' } }}
            InputProps={{
              sx: { fontSize: "1rem" },
              endAdornment: isMobileQuery ? (
                <InputAdornment position="end">
                  <IconButton 
                    size="small" 
                    onClick={handleSearch}
                    title="Search"
                    edge="end"
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined
            }}
          />
          <Stack direction="row" spacing={0.5} sx={{ display: { xs: 'none', sm: 'flex' } }}>
            <IconButton 
              size="small" 
              onClick={handleSearch}
              title="Search"
            >
              <SearchIcon fontSize="small" />
            </IconButton>
            
            {/* Refresh Button - Hidden on mobile, visible on desktop */}
            {!isMobileQuery && (
              <IconButton 
                size="small" 
                onClick={fetchTree}
                title="Refresh Tree"
                disabled={loading}
                sx={{ 
                  ...(loading && { opacity: 0.6 })
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            )}
            
            {/* Zoom Controls - Hidden on mobile, visible on desktop */}
            {!isMobileQuery && (
              <>
                <IconButton 
                  size="small" 
                  onClick={handleZoomIn} 
                  title="Zoom In"
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={handleZoomOut} 
                  title="Zoom Out"
                >
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={handleZoomReset} 
                  title="Reset Zoom"
                >
                  <FitScreenIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Stack>
        </Stack>
      </Stack>

      {/* Network Summary Statistics */}
      {treeData && treeData[0] && (
        <Stack 
          direction="row" 
          spacing={3} 
          alignItems="center" 
          flexWrap="nowrap" 
          mb={1}
          sx={{ 
            px: 0, 
            overflowX: "auto",
            overflowY: "hidden",
            flexShrink: 0,
            WebkitOverflowScrolling: "touch",
            "&::-webkit-scrollbar": {
              height: "8px",
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
              📊 Network
            </Typography>
          </Box>
          <Box textAlign="center" sx={{ minWidth: "auto" }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
              Total
            </Typography>
            <Typography variant="h6" color="primary" sx={{ lineHeight: 1, fontWeight: "bold" }}>
              {countDescendants(treeData[0])}
            </Typography>
          </Box>
          <Box textAlign="center" sx={{ minWidth: "auto" }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
              Depth
            </Typography>
            <Typography variant="h6" color="info.main" sx={{ lineHeight: 1, fontWeight: "bold" }}>
              {levelCounts.findIndex(count => count === 0) !== -1 ? levelCounts.findIndex(count => count === 0) : levelCounts.length}
            </Typography>
          </Box>
        </Stack>
      )}

      {/* Breadcrumbs for navigation */}
          {focusedNodeId && navigationPath.length > 0 && (
            <Box 
              sx={{ 
                mb: 1, 
                px: 1,
                py: 0.5,
                backgroundColor: "background.paper",
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
              }}
            >
              <Breadcrumbs 
                separator="›" 
                aria-label="breadcrumb"
                sx={{ fontSize: "0.75rem" }}
              >
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => {
                    setFocusedNodeId(null);
                    setNavigationPath([]);
                    setClickedNodeId(null);
                    setHighlightedDownlineIds(new Set());
                  }}
                  sx={{ 
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: "pointer",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <HomeIcon fontSize="small" />
                  Root
                </Link>
                {navigationPath.map((nodeId, idx) => {
                  const node = findNodeById(treeData, nodeId);
                  if (!node) return null;
                  const isLast = idx === navigationPath.length - 1;
                  return (
                    <Link
                      key={nodeId}
                      component="button"
                      variant="body2"
                      onClick={() => {
                        if (!isLast) {
                          const newPath = navigationPath.slice(0, idx + 1);
                          setNavigationPath(newPath);
                          setFocusedNodeId(nodeId);
                          setClickedNodeId(null);
                          setHighlightedDownlineIds(new Set());
                        }
                      }}
                      sx={{ 
                        cursor: isLast ? "default" : "pointer",
                        textDecoration: "none",
                        fontWeight: isLast ? "bold" : "normal",
                        color: isLast ? "primary.main" : "text.primary",
                        "&:hover": isLast ? {} : { textDecoration: "underline" },
                      }}
                    >
                      {node.name || `User ${nodeId}`}
                    </Link>
                  );
                })}
              </Breadcrumbs>
            </Box>
          )}



      {/* Tree Container - Same view for all devices */}
      <Box
        ref={treeContainerRef}
        flex={1}
        width="100%"
        overflow="auto"
        sx={{ 
          backgroundColor: "#fafafa",
            minHeight: "75vh",
            maxHeight: "75vh",
            height: "auto",
          border: 1,
          borderColor: "grey.300",
          borderRadius: 1,
          touchAction: "pan-x pan-y",
          WebkitOverflowScrolling: "touch",
          position: "relative",
          boxSizing: "border-box",
          overflowX: "auto",
          overflowY: "auto",
          p: 0,
        }}
      >
        {filteredTreeData && filteredTreeData.length > 0 ? (
              <Box
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                sx={{
                  width: "100%",
                  height: "100%",
                  "& svg": {
                    touchAction: "none",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                  }
                }}
              >
                {console.log('🎨 [RENDER] Tree component data:', {
                  hasData: !!filteredTreeData,
                  length: filteredTreeData?.length,
                  rootChildren: filteredTreeData?.[0]?.children?.length || 0,
                  rootChildrenSample: filteredTreeData?.[0]?.children?.slice(0, 3).map(c => ({ id: c.id, name: c.name })) || []
                })}
                <Tree
                  data={filteredTreeData}
                  translate={isMobileQuery ? { 
                    x: containerWidth / 2, 
                    y: 150 
                  } : { 
                    x: 500, 
                    y: 100 
                  }}
                  orientation="vertical"
                  pathFunc="elbow"
                  collapsible={true}
                  zoomable={true}
                  draggable={true}
                  scaleExtent={{ min: 0.2, max: 2 }}
                  zoom={zoom}
                  nodeSize={{ x: 300, y: 280 }}
                  separation={{ siblings: 1.5, nonSiblings: 1.5 }}
                  renderCustomNodeElement={renderCustomNode}
                  pathClassFunc={pathClassFunc}
                  styles={treeStyles}
                  depthFactor={200}
                  transitionDuration={300}
                  enableLegacyTransitions={true}
                />
              </Box>
        ) : (
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center" 
            height="400px"
            color="text.secondary"
          >
            <Typography>No tree data available</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ReferralTreeVisual;
